import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../../../layout/TopBar';
import useNotifications, {
  Notification,
  NotificationCategory,
} from '../../../macadamia-core/components/hooks/useNotifications';
import Scale from '../../../macadamia-core/icons/Scale';
import Document from '../../../macadamia-core/icons/Document';
import Cog6Tooth from '../../../macadamia-core/icons/Cog6Tooth';
import UserGroup from '../../../macadamia-core/icons/UserGroup';

const CATEGORIES = [
  { key: null, label: 'Toutes' },
  { key: 'jurisprudences', label: 'Jurisprudences' },
  { key: 'dossiers', label: 'Dossiers' },
  { key: 'abonnements', label: 'Abonnements' },
  { key: 'system', label: 'Système' },
] as const;

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<NotificationCategory>(null);
  const {
    notifications,
    isLoading,
    hasMore,
    loadMore,
    markAsRead,
    markAllAsRead,
    totalUnreadCount,
    fetchNotifications,
  } = useNotifications();

  // Charger les notifications au montage et quand la catégorie change
  useEffect(() => {
    fetchNotifications(1, selectedCategory);
  }, [selectedCategory, fetchNotifications]);

  const handleCategoryChange = (category: NotificationCategory) => {
    setSelectedCategory(category);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead(selectedCategory);
    fetchNotifications(1, selectedCategory);
  };

  const handleNotificationClick = (notification: Notification) => {
    if (notification.status === 'unread') {
      markAsRead(notification._id);
    }

    // Navigation selon le type
    if (notification.type === 'new_follower' && notification.relatedEntityId) {
      navigate(`/profil/${notification.relatedEntityId}`);
    } else if (
      notification.type === 'jurisprudence_alert_digest' ||
      notification.type === 'jurisprudence_alert_immediate'
    ) {
      navigate('/jurisprudences/alertes');
    } else if (
      notification.type === 'nouvelle_jurisprudence_similaire' ||
      notification.type === 'nouvelle_jurisprudence_similaire_detail'
    ) {
      navigate('/jurisprudences/suivi');
    } else if (notification.relatedEntityId) {
      navigate(`/dossiers/${notification.relatedEntityId}`);
    }
  };

  const getNotificationIcon = (type: string) => {
    if (
      type === 'nouvelle_jurisprudence_similaire' ||
      type === 'nouvelle_jurisprudence_similaire_detail' ||
      type === 'jurisprudence_alert_digest' ||
      type === 'jurisprudence_alert_immediate'
    ) {
      return <Scale fill="currentColor" width="20px" height="20px" />;
    }
    if (type === 'invitation' || type === 'suggestion') {
      return <Document fill="currentColor" width="20px" height="20px" />;
    }
    if (type === 'new_follower') {
      return <UserGroup fill="currentColor" width="20px" height="20px" />;
    }
    return <Cog6Tooth stroke="currentColor" width="20px" height="20px" />;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="min-h-screen bg-mcdm-primary-100 dark:bg-mcdm-third-500">
      <TopBar />
      <div className="pt-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-platypi text-mcdm-third-500 dark:text-mcdm-primary-100">
              Notifications
            </h1>
            {totalUnreadCount > 0 && (
              <span className="px-2.5 py-0.5 text-sm font-medium bg-red-500 text-white rounded-full">
                {totalUnreadCount}
              </span>
            )}
          </div>
          {totalUnreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="text-sm text-mcdm-secondary-500 dark:text-mcdm-secondary-400 hover:underline"
            >
              Tout marquer comme lu
            </button>
          )}
        </div>

        {/* Filtres par catégorie */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {CATEGORIES.map(cat => (
            <button
              key={cat.key ?? 'all'}
              onClick={() => handleCategoryChange(cat.key as NotificationCategory)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === cat.key
                  ? 'bg-mcdm-secondary-500 text-white'
                  : 'bg-white dark:bg-mcdm-third-400 text-mcdm-third-500 dark:text-mcdm-primary-200 hover:bg-gray-100 dark:hover:bg-mcdm-third-300'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Liste des notifications */}
        <div className="bg-white dark:bg-mcdm-third-400 rounded-lg shadow-sm overflow-hidden">
          {notifications.length === 0 && !isLoading ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              Aucune notification
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-mcdm-third-300">
              {notifications.map(notification => (
                <li
                  key={notification._id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-4 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-mcdm-third-300 ${
                    notification.status === 'unread'
                      ? 'bg-mcdm-primary-200 dark:bg-mcdm-third-300'
                      : 'dark:bg-mcdm-third-500'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Icône */}
                    <div
                      className={`flex-shrink-0 p-2 rounded-full ${
                        notification.status === 'unread'
                          ? 'bg-mcdm-secondary-100 text-mcdm-secondary-500 dark:bg-mcdm-secondary-500/20 dark:text-mcdm-secondary-400'
                          : 'bg-gray-100 text-gray-500 dark:bg-mcdm-third-300 dark:text-gray-400'
                      }`}
                    >
                      {getNotificationIcon(notification.type)}
                    </div>

                    {/* Contenu */}
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm ${
                          notification.status === 'unread'
                            ? 'font-medium text-mcdm-third-500 dark:text-mcdm-primary-100'
                            : 'text-mcdm-third-400 dark:text-mcdm-primary-200'
                        }`}
                      >
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {formatDate(notification.createdAt)}
                      </p>
                    </div>

                    {/* Indicateur non lu */}
                    {notification.status === 'unread' && (
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-mcdm-secondary-500 rounded-full" />
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Loading / Load more */}
          {isLoading && (
            <div className="p-4 text-center">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-mcdm-secondary-500 border-t-transparent" />
            </div>
          )}

          {hasMore && !isLoading && notifications.length > 0 && (
            <button
              onClick={loadMore}
              className="w-full p-4 text-center text-sm text-mcdm-secondary-500 dark:text-mcdm-secondary-400 hover:bg-gray-50 dark:hover:bg-mcdm-third-300"
            >
              Charger plus
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
